
// ---------- Shared domain types ----------

export type ID = string;

export type StatusKey = 'not_started' | 'working' | 'stuck' | 'done' | 'na';

export interface Person {
  id: ID;
  name: string;
  role: 'principal' | 'broker' | 'marketing';
  color: string; // avatar bg
}

// A single line item in the fixed proposal checklist template
export interface ChecklistItem {
  id: ID;
  key: string; // template key
  label: string;
  hint?: string; // helper describing what this step means
  status: StatusKey;
  links: LinkRef[];
  notes: NoteEntry[];
  updatedAt?: string; // ISO
  updatedById?: ID;
}

export interface LinkRef {
  id: ID;
  label: string;
  url: string;
  kind: 'powerpoint' | 'dropbox' | 'sheet' | 'sharepoint' | 'other';
  addedById: ID;
  addedAt: string; // ISO
}

export interface NoteEntry {
  id: ID;
  text: string;
  authorId: ID;
  createdAt: string; // ISO
}

export interface ActivityEntry {
  id: ID;
  proposalId: ID;
  itemKey?: string;
  authorId: ID;
  text: string;
  createdAt: string; // ISO
}

export type ProposalStage =
  | 'requested'
  | 'in_progress'
  | 'om_sent'
  | 'ela_signed'
  | 'listed'
  | 'live';

export interface Proposal {
  id: ID;
  property: string;
  address: string;
  assetType: string;
  requestedById: ID; // broker
  requestedAt: string; // ISO
  stage: ProposalStage;
  deckType: 'Proposal' | 'BOV' | 'Pitch Deck';
  checklist: ChecklistItem[];
}

export type PropertyStatus = 'active' | 'under_contract' | 'sold';

export interface PropertyRecord {
  id: ID;
  name: string;
  address: string;
  assetType: string;
  status: PropertyStatus;
  price: string;
  brokerId: ID;
  platforms: string[]; // where it was listed
  listedAt?: string;
}

export type LeadStatus = 'new' | 'contacted' | 'qualified' | 'won' | 'lost';

export interface Lead {
  id: ID;
  contact: string;
  company: string;
  interest: string;
  status: LeadStatus;
  ownerId: ID;
  value: string;
  createdAt: string;
}

export type SocialChannel = 'flyer' | 'email' | 'linkedin' | 'instagram';
export type SocialStatus = 'planned' | 'scheduled' | 'posted';

export interface SocialPost {
  id: ID;
  proposalId?: ID;
  title: string;
  channel: SocialChannel;
  date: string; // ISO date (day)
  status: SocialStatus;
  ownerId: ID;
  example?: string; // what was posted
  metrics?: { impressions?: number; engagements?: number; clicks?: number };
}

export const STATUS_META: Record<StatusKey, { label: string; bg: string; text: string; dot: string }> = {
  not_started: { label: 'Not started', bg: 'bg-slate-100', text: 'text-slate-600', dot: 'bg-slate-400' },
  working: { label: 'Working on it', bg: 'bg-amber-100', text: 'text-amber-700', dot: 'bg-amber-500' },
  stuck: { label: 'Stuck', bg: 'bg-rose-100', text: 'text-rose-700', dot: 'bg-rose-500' },
  done: { label: 'Done', bg: 'bg-emerald-100', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  na: { label: 'N/A', bg: 'bg-slate-100', text: 'text-slate-400', dot: 'bg-slate-300' },
};

export const STAGE_META: Record<ProposalStage, { label: string; color: string }> = {
  requested: { label: 'Requested', color: 'bg-slate-100 text-slate-600' },
  in_progress: { label: 'In progress', color: 'bg-blue-100 text-blue-700' },
  om_sent: { label: 'OM sent', color: 'bg-violet-100 text-violet-700' },
  ela_signed: { label: 'ELA signed', color: 'bg-amber-100 text-amber-700' },
  listed: { label: 'Listed', color: 'bg-cyan-100 text-cyan-700' },
  live: { label: 'Live', color: 'bg-emerald-100 text-emerald-700' },
};

// The fixed checklist template every new request generates
export const CHECKLIST_TEMPLATE: { key: string; label: string; hint?: string }[] = [
  { key: 'deck', label: 'Proposal / BOV / Pitch Deck', hint: 'Attach the PowerPoint or Dropbox link' },
  { key: 'due_diligence', label: 'Due Diligence Items', hint: 'Collect and file DD materials' },
  { key: 'war_room', label: 'Set up War Room', hint: 'Create the shared deal war room' },
  { key: 'om', label: 'Offering Memorandum', hint: 'Finalize and distribute the OM' },
  { key: 'ela', label: 'ELA (Exclusive Listing Agreement)', hint: 'Broker sends to client to sign, then returns to Faraz, Michael & Simon' },
  { key: 'listing', label: 'List the Property', hint: 'Track every platform the property is listed on' },
  { key: 'signs', label: 'Signs for Property', hint: 'Yes / No — confirm with broker' },
  { key: 'flyer', label: 'Flyer Approval', hint: 'Review and approve the marketing flyer' },
  { key: 'socials', label: 'Socials Kickoff', hint: 'Flyer, Email Campaign, LinkedIn, Instagram' },
];

export const LISTING_PLATFORMS = [
  'LoopNet',
  'CoStar',
  'Crexi',
  'Company Website',
  'Brevitas',
  'CommercialSearch',
  'Ten-X',
];
